import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineUserGroup, HiOutlineChevronDown, HiXMark } from "react-icons/hi2";
import { FaMapMarkerAlt, FaCalendarAlt, FaSearch, FaBed, FaHotel } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";

const HotelSearch = () => {
  const navigate = useNavigate();
  const travelerRef = useRef(null);
  const [showTravelerModal, setShowTravelerModal] = useState(false);
  const [citySearch, setCitySearch] = useState({ query: "", results: [], loading: false });
  const [formData, setFormData] = useState({
    city: null,
    checkIn: "",
    checkOut: "",
    rooms: 1,
    adults: 2, // Standard default for hotels
    children: 0,
    infants: 0
  });

  useEffect(() => {
    if (window.flatpickr) {
      window.flatpickr(".date-input", {
        dateFormat: "Y-m-d",
        minDate: "today",
        disableMobile: "true",
        onChange: (selectedDates, dateStr, instance) => {
          const fieldName = instance.element.getAttribute("name");
          setFormData(prev => ({ ...prev, [fieldName]: dateStr }));
        }
      });
    }
  }, []);

  useEffect(() => {
    const closeModals = (e) => {
      if (travelerRef.current && !travelerRef.current.contains(e.target)) {
        setShowTravelerModal(false);
      }
    };
    document.addEventListener("mousedown", closeModals);
    return () => document.removeEventListener("mousedown", closeModals);
  }, []);

  const handleCitySearch = async (query) => {
    setCitySearch(prev => ({ ...prev, query }));
    if (query.length > 2) {
      try {
        const res = await axios.get(`${baseurl}/searchHotelCityList?q=${query}`);
        setCitySearch(prev => ({ ...prev, results: res.data.data || [] }));
      } catch (err) { console.error(err); }
    } else {
      setCitySearch(prev => ({ ...prev, results: [] }));
    }
  };

  const selectCity = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setCitySearch({ query: `${city.city_name}, ${city.country_name}`, results: [] });
  };

  const updateCount = (field, op) => {
    setFormData(prev => ({
      ...prev,
      [field]: op === "inc" ? prev[field] + 1 : Math.max(field === "adults" || field === "rooms" ? 1 : 0, prev[field] - 1)
    }));
  };

  const onSearchHotels = () => {
    const { city, checkIn, checkOut, rooms, adults, children, infants } = formData;
    if (!city || !checkIn || !checkOut) {
      Swal.fire({ icon: 'warning', title: 'Incomplete Search', text: 'Select destination and dates.', confirmButtonColor: '#1a73e8' });
      return;
    }
    const queryStr = new URLSearchParams({
      cityCode: city.city_code, cityName: city.city_name, checkIn, checkOut,
      rooms: rooms.toString(), adults: adults.toString(), children: children.toString(), infants: infants.toString()
    }).toString();
    navigate(`/hotel-result?${queryStr}`);
  };

  return (
    <>
      <style>{`

      .hero-section {
          position: relative;
          max-height: 400px;
          background: linear-gradient(rgba(11, 61, 71, 0.8), rgba(22, 96, 112, 0.9)), 
                      url('https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=2000');
          background-size: cover;
          background-position: center;
          padding: 150px 0 150px 0;
          color: white;
          text-align: center;
        }

        .hero-content h1 {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 15px;
          text-shadow: 0 2px 10px rgba(0,0,0,0.2);
        }

        .hero-content p {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 40px;
        }

        .floating-booking-container {
          max-width: 1100px;
          margin: -80px auto 50px auto;
          position: relative;
          z-index: 10;
          padding: 0 15px;
        }


        .hotel-hero-card {
          background: #ffffff;
          border-radius: 24px;
          padding: 35px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
          max-width: 1150px;
          margin: 40px auto;
          border: 1px solid #f0f0f0;
        }

        .hotel-grid-slick {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.2fr auto;
          gap: 12px;
          align-items: stretch;
        }

        .input-wrap-slick {
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 16px;
          padding: 10px 16px;
          transition: all 0.25s ease;
          cursor: pointer;
          position: relative;
        }

        .input-wrap-slick:hover { border-color: #cbd5e1; background: #f1f5f9; }
        .input-wrap-slick:focus-within {
          border-color: #1a73e8;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.1);
        }

        .input-wrap-slick label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .input-wrap-slick input, .val-display {
          border: none;
          background: transparent;
          width: 100%;
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          outline: none !important;
          padding: 0;
        }

        .search-results-dropdown {
          position: absolute;
          top: 110%;
          left: 0;
          right: 0;
          background: white;
          border-radius: 16px;
          z-index: 1000;
          max-height: 300px;
          overflow-y: auto;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
          padding: 8px;
        }

        .result-item {
          padding: 12px 16px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: 0.2s;
        }

        .result-item:hover { background: #eff6ff; }
        .result-item .icon-circle {
          width: 32px; height: 32px; background: #e0e7ff;
          color: #4338ca; border-radius: 50%; display: flex; align-items: center; justify-content: center;
        }

        .traveler-dropdown {
          position: absolute;
          top: 110%;
          right: 0;
          width: 320px;
          background: white;
          border-radius: 20px;
          padding: 24px;
          z-index: 1001;
          box-shadow: 0 20px 50px rgba(0,0,0,0.2);
        }

        .counter-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .counter-ctrl {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .ctrl-btn {
          width: 32px; height: 32px; border-radius: 50%; border: 1px solid #cbd5e1;
          background: white; color: #1e293b; font-size: 18px; display: flex;
          align-items: center; justify-content: center; transition: 0.2s;
        }

        .ctrl-btn:hover:not(:disabled) { background: #1a73e8; color: white; border-color: #1a73e8; }

        .btn-hotel-search {
          background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
          color: white; border: none; border-radius: 16px;
          padding: 0 35px; height: 62px; font-weight: 700;
          transition: all 0.3s ease; display: flex; align-items: center; gap: 10px;
        }

        .btn-hotel-search:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(26, 115, 232, 0.4); }

        @media (max-width: 991px) {
          .hotel-grid-slick { grid-template-columns: 1fr; gap: 10px; }
          .hotel-hero-card { padding: 20px; margin: 15px; }
          .traveler-dropdown { width: 100%; left: 0; }
          .btn-hotel-search { width: 100%; justify-content: center; }
        }
      `}</style>


<section className="hero-section">
        <div className="container hero-content2">
          <h1>India's No. 1 Hotel/Stay Booking Sites</h1>
          <p>Over 25000+ property listed across the country.</p>
        </div>
      </section>


<div className="floating-booking-container"> 
      <div className="hotel-hero-card  " >
        <h4 className="fw-bold mb-4" style={{color: '#0f172a'}}>
          <FaHotel className="me-2 text-primary" /> Book Best Quality Hotels
        </h4>

        <div className="hotel-grid-slick">
          {/* LOCATION */}
          <div className="position-relative">
            <div className="input-wrap-slick">
              <label><FaMapMarkerAlt /> Destination</label>
              <input
                type="text"
                placeholder="Where are you going?"
                value={citySearch.query}
                onChange={(e) => handleCitySearch(e.target.value)}
              />
            </div>
            {citySearch.results.length > 0 && (
              <div className="search-results-dropdown shadow-lg">
                {citySearch.results.map(item => (
                  <div className="result-item" key={item.city_code} onClick={() => selectCity(item)}>
                    <div className="icon-circle"><FaMapMarkerAlt size={14} /></div>
                    <div>
                      <div className="fw-bold text-dark">{item.city_name}</div>
                      <div className="text-muted small">{item.country_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CHECK IN */}
          <div className="input-wrap-slick">
            <label><FaCalendarAlt /> Check-in</label>
            <input name="checkIn" className="date-input" placeholder="Add Date" readOnly />
          </div>

          {/* CHECK OUT */}
          <div className="input-wrap-slick">
            <label><FaCalendarAlt /> Check-out</label>
            <input name="checkOut" className="date-input" placeholder="Add Date" readOnly />
          </div>

          {/* GUESTS */}
          <div className="position-relative" ref={travelerRef}>
            <div className="input-wrap-slick" onClick={() => setShowTravelerModal(!showTravelerModal)}>
              <label><HiOutlineUserGroup /> Guests & Rooms</label>
              <div className="val-display d-flex align-items-center justify-content-between">
                <span>{formData.rooms}R, {formData.adults + formData.children}G</span>
                <HiOutlineChevronDown size={14} color="#64748b" />
              </div>
            </div>

            {showTravelerModal && (
              <div className="traveler-dropdown animate__animated animate__fadeInUp animate__faster">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="fw-bold">Room Selection</span>
                  <HiXMark className="cursor-pointer" onClick={() => setShowTravelerModal(false)} />
                </div>
                
                {[
                  { label: "Rooms", key: "rooms", sub: "Number of rooms" },
                  { label: "Adults", key: "adults", sub: "Ages 12+" },
                  { label: "Children", key: "children", sub: "Ages 2-12" }
                ].map((row) => (
                  <div className="counter-row" key={row.key}>
                    <div>
                      <div className="fw-bold small">{row.label}</div>
                      <div className="text-muted" style={{fontSize: '11px'}}>{row.sub}</div>
                    </div>
                    <div className="counter-ctrl">
                      <button className="ctrl-btn" onClick={() => updateCount(row.key, "dec")}>-</button>
                      <span className="fw-bold" style={{minWidth: '20px', textAlign: 'center'}}>{formData[row.key]}</span>
                      <button className="ctrl-btn" onClick={() => updateCount(row.key, "inc")}>+</button>
                    </div>
                  </div>
                ))}
                
                <button className="btn btn-primary w-100 mt-3 py-2 rounded-pill fw-bold" onClick={() => setShowTravelerModal(false)}>
                  Apply Details
                </button>
              </div>
            )}
          </div>

          {/* SEARCH BTN */}
          <button className="btn-hotel-search" onClick={onSearchHotels}>
            <FaSearch /> SEARCH
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default HotelSearch;